import React, { useContext, useEffect, useRef } from 'react'
import { Form, Formik } from 'formik'
import useRecaptcha from '../../../Recaptcha/hooks/useRecaptcha'
import Recaptcha from '../../../Recaptcha'
import { userSigninSchema, userSigninSchemaEN } from './schema.jsx'
import { getAllowedCountryCodes, getAllowedInitialCountryCode, getDefaultSignupCurrency } from '../../../../utils/signup.js'
import {
  checkIsEmailUnique,
  checkIsUsernameUnique
} from '../../../../utils/apiCalls.js'
import { toast } from '../../../Toast/index.jsx'
import { getItem } from '../../../../utils/storageUtils.js'
import SignupFooter from '../../../FormFieldControl/SignupFooter/index.jsx'
import { SignUpContext } from '../../../../pages/NewSignup/useContext/index.js'
import MobileSignupImage from './components/MobileSignupImage/index.jsx'
// import ProcessBar from './components/ProcessBar/index.jsx'
import LoginDetail from './components/LoginDetail/index.jsx'
import PersonalDetail from './components/PersonalDetail/Index.jsx'
import { PersistFormikValues } from 'formik-persist-values'
import { isMobileOrTablet } from '../../../../utils/helpers.js'
import LoginHeading from '../../../LoginHeading/LoginHeading.jsx'
import useSignupTermsAndConditions from 'hooks/useSignupTermsAndConditions'

const SignupForm = () => {
  const {
    regFields,
    tenantDetails,
    generalConfig,
    languageData,
    countryCode,
    allCountries,
    handleInitialState,
    language,
    selectedLanguageCode,
    languageCode,
    setLanguage,
    loading,
    disableSubmit,
    setDisableSubmit
  } = useContext(SignUpContext)

  const formik = useRef()
  const isFinalStep = true

  const mobileOrTablet = isMobileOrTablet()
  const { recaptchaRef, handleRecaptchaChange, getRecaptchaToken } = useRecaptcha()
  const { defaultConditions, acceptConditions, setAcceptConditions } = useSignupTermsAndConditions()

  useEffect(() => {
    document.body.classList.add('login-signup')
    return function cleanup () {
      window.localStorage.removeItem('signup-form')
      document.body.classList.remove('login-signup')
    }
  }, [])

  // Only pre-fill the geo-detected country if it survived the allowedCountryMapping
  // filter (i.e. it is actually selectable). Otherwise leave it empty so a restricted
  // country (e.g. NL on wunderwins) is never silently pre-submitted.
  const allowedCountryCodes = getAllowedCountryCodes(allCountries)
  const allowedCountryCode = getAllowedInitialCountryCode(countryCode, allCountries)
  const activeLanguage = selectedLanguageCode || languageCode || language || getItem('language')

  const initialValues = {
    email: '',
    password: '',
    username: '',
    firstName: '',
    lastName: '',
    phone: null,
    phoneCode: '',
    dateOfBirth: null,
    address: '',
    confirmPassword: '',
    countryCode: allowedCountryCode,
    currencyCode: getDefaultSignupCurrency({
      activeLanguage,
      allowedCountryCode,
      tenantDetails,
      generalConfig
    }),
    gender: '',
    nationality: '',
    preferredLanguage: '',
    newsLetter: defaultConditions.newsLetter,
    sms: defaultConditions.sms,
    newsletterAndSms: defaultConditions.newsletterAndSms,
    zipCode: '',
    city: '',
    affiliateCode: ''
  }

  const validationSchema = () => {
    return [' ', '', 'EN'].includes(getItem('language')?.toUpperCase())
      ? userSigninSchemaEN(regFields, languageData, allowedCountryCodes)
      : userSigninSchema(regFields, languageData, allowedCountryCodes)
  }

  const onSubmit = async (formValues) => {
    setDisableSubmit(true)

    try {
      await checkIsEmailUnique({ email: formik?.current?.values?.email })
    } catch (error) {
      await formik?.current?.setFieldTouched('email', true)
      formik?.current?.setFieldError('email', languageData?.emailAlreadyRegistered)
      setDisableSubmit(false)
      return
    }
    try {
      await checkIsUsernameUnique({ username: formik?.current?.values?.username })
    } catch (error) {
      await formik?.current?.setFieldTouched('username', true)
      formik?.current?.setFieldError('username', languageData?.userNameAlreadyTaken)
      setDisableSubmit(false)
      return
    }

    // Capture recaptcha if enabled
    const token = await getRecaptchaToken()

    if (process.env.REACT_APP_GOOGLE_RECAPTCHA_SITE_KEY && !token) {
      toast(languageData?.recaptchaError || 'Please verify you are not a robot', 'error')
      setDisableSubmit(false)
      return
    }

    let toastMessage = `${languageData?.pleaseAccept}`

    // if (!acceptConditions.privacyPolicy) {
    //   toastMessage += ` ${languageData?.acceptPrivacyPolicy}`
    // }
    if (!acceptConditions.termsAndConditions) {
      toastMessage += ` ${languageData?.acceptTerms}`
    }

    let modeOfCommShouldValidate = false

    if (regFields?.sms === 2 || regFields?.newsLetter === 2 || regFields?.newsletterAndSms === 2) {
      modeOfCommShouldValidate = true
    }

    if (
      modeOfCommShouldValidate &&
      (
        (regFields?.newsLetter === 2 && !acceptConditions?.newsLetter) ||
        (regFields?.sms === 2 && !acceptConditions?.sms) ||
        (regFields?.newsletterAndSms === 2 && !acceptConditions?.newsletterAndSms)
      )
    ) {
      if (
        acceptConditions.termsAndConditions /* && acceptConditions.privacyPolicy */
      ) {
        toastMessage += `${languageData?.modeOfComm}`.replace('and', '')
      } else {
        toastMessage += ` ${languageData?.modeOfComm}`
      }
    } else {
      modeOfCommShouldValidate = false
    }

    if (
      // !acceptConditions.privacyPolicy ||
      !acceptConditions.termsAndConditions ||
      modeOfCommShouldValidate
    ) {
      toast(`${toastMessage} ${languageData?.beforeNextStep}`, 'error')
      setDisableSubmit(false)
    } else {
      // Pass token to handleInitialState
      handleInitialState({ ...formValues, recaptchaToken: token })
    }
  }

  return (
    <Formik
      enableReinitialize
      innerRef={formik}
      initialValues={initialValues}
      validationSchema={validationSchema}
      validateOnMount
      validateOnBlur
      validateOnChange
      onSubmit={onSubmit}
    >
      {(formik) => {
        return (
          <>
            <Form className='signup-form'>
              <LoginHeading
                language={language}
                setLanguage={setLanguage}
                langKey='signupStartWithEmail'
              />
              {mobileOrTablet && <MobileSignupImage />}

              {regFields && (
                <div className='signup-right-section'>
                  <div className='form'>
                    <div>
                      {/* <ProcessBar isFinalStep={isFinalStep} /> */}

                      <LoginDetail
                        formik={formik}
                      />
                      <PersonalDetail
                        formik={formik}
                        acceptConditions={acceptConditions}
                        setAcceptConditions={setAcceptConditions}
                      />
                      {/* )} */}
                    </div>

                    <Recaptcha
                      innerRef={recaptchaRef}
                      onChange={handleRecaptchaChange}
                    />

                    <SignupFooter
                      isFinalStep={isFinalStep}
                      formik={formik}
                      disabled={disableSubmit || loading}
                    />
                  </div>
                </div>
              )}
            </Form>
            <PersistFormikValues
              ignoreValues={[
                'address',
                'city',
                'countryCode',
                'currencyCode',
                'dateOfBirth',
                'firstName',
                'gender',
                'lastName',
                'nationality',
                'newsLetter',
                'password',
                'phone',
                'phoneCode',
                'preferredLanguage',
                'sms',
                'confirmPassword',
                'zipCode'
              ]}
              persistInvalid
              name='signup-form'
            />
          </>
        )
      }}
    </Formik>
  )
}

export default SignupForm
